import React, { useMemo } from 'react';
import { Truck, DollarSign } from 'lucide-react';

/**
 * SupplierPricingPanel
 *
 * Extracted from ProductImport.js — handles supplier selection,
 * margin slider, and TTC price calculation.
 *
 * Props:
 *   suppliers            – array of supplier objects ({ supplier_id, name, type, default_margin_pct })
 *   supplierPrices       – array of { supplier_id, supplier_name, price_ht }
 *   onSupplierPricesChange – (updater) => void   (receives prev => next, like setState)
 *   selectedSupplier     – string | null
 *   onSelectedSupplierChange – (id | null) => void
 *   marginPct            – number (5-50, default 25)
 *   onMarginChange       – (number) => void
 *   manualPriceHT        – string
 *   onManualPriceChange  – (string) => void
 *   onApplyPrice         – (priceTTC: string) => void
 */
export default function SupplierPricingPanel({
  suppliers = [],
  supplierPrices = [],
  onSupplierPricesChange,
  selectedSupplier,
  onSelectedSupplierChange,
  marginPct = 25,
  onMarginChange,
  manualPriceHT = '',
  onManualPriceChange,
  onApplyPrice,
}) {
  // ── Calculated price ──
  const calculatedPrice = useMemo(() => {
    let baseHT = null;

    if (selectedSupplier) {
      const entry = supplierPrices.find(p => p.supplier_id === selectedSupplier);
      if (entry?.price_ht) baseHT = entry.price_ht;
    } else if (manualPriceHT) {
      baseHT = parseFloat(manualPriceHT);
    }

    if (!baseHT || isNaN(baseHT)) return null;

    const withMargin = (baseHT * (1 + marginPct / 100)).toFixed(2);
    const ttc = (parseFloat(withMargin) * 1.20).toFixed(2);
    return { withMargin, ttc };
  }, [selectedSupplier, supplierPrices, manualPriceHT, marginPct]);

  return (
    <div className="bg-white rounded-sm border border-slate-200 p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-heading font-semibold text-white text-sm flex items-center gap-2">
          <Truck className="w-4 h-4 text-secondary" /> Tarification grossistes
        </h4>
        {calculatedPrice && (
          <div className="text-right">
            <span className="text-2xl font-bold text-secondary">{calculatedPrice.ttc} EUR TTC</span>
            <span className="text-xs text-slate-500 block">({calculatedPrice.withMargin} EUR HT + 20% TVA)</span>
          </div>
        )}
      </div>

      {/* Supplier price entries */}
      {suppliers.length > 0 ? (
        <div className="space-y-2">
          {suppliers.map(s => {
            const existingPrice = supplierPrices.find(p => p.supplier_id === s.supplier_id);
            const isSelected = selectedSupplier === s.supplier_id;
            return (
              <div
                key={s.supplier_id}
                className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                  isSelected ? 'border-secondary bg-secondary/10' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => {
                  onSelectedSupplierChange(isSelected ? null : s.supplier_id);
                  if (!isSelected && s.default_margin_pct) onMarginChange(s.default_margin_pct);
                }}
              >
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${isSelected ? 'border-secondary bg-amber-500' : 'border-slate-300'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">{s.name}</p>
                  <p className="text-[10px] text-slate-500">{s.type} — Marge defaut: {s.default_margin_pct}%</p>
                </div>
                <div className="shrink-0 w-28">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Prix HT"
                    value={existingPrice?.price_ht || ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      const val = e.target.value;
                      onSupplierPricesChange(prev => {
                        const idx = prev.findIndex(p => p.supplier_id === s.supplier_id);
                        const entry = { supplier_id: s.supplier_id, supplier_name: s.name, price_ht: parseFloat(val) || 0 };
                        if (idx >= 0) { const copy = [...prev]; copy[idx] = entry; return copy; }
                        return [...prev, entry];
                      });
                    }}
                    className="w-full bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5 text-xs text-white outline-none text-right"
                  />
                </div>
                <span className="text-[10px] text-slate-500 shrink-0">EUR HT</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-xs text-slate-500">Aucun fournisseur configure.</p>
          <a href="/my/fournisseurs" className="text-xs text-secondary hover:text-secondary underline">Configurer les fournisseurs</a>
        </div>
      )}

      {/* Manual price entry (fallback) */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
        <span
          className={`w-4 h-4 rounded-full border-2 shrink-0 cursor-pointer ${!selectedSupplier ? 'border-secondary bg-amber-500' : 'border-slate-300'}`}
          onClick={() => onSelectedSupplierChange(null)}
        />
        <span className="text-sm text-slate-600 flex-1 cursor-pointer" onClick={() => onSelectedSupplierChange(null)}>
          Prix manuel (sans fournisseur)
        </span>
        <div className="shrink-0 w-28">
          <input
            type="number"
            step="0.01"
            placeholder="Prix HT"
            value={manualPriceHT}
            onChange={e => { onManualPriceChange(e.target.value); onSelectedSupplierChange(null); }}
            className="w-full bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5 text-xs text-white outline-none text-right"
          />
        </div>
        <span className="text-[10px] text-slate-500 shrink-0">EUR HT</span>
      </div>

      {/* Margin slider */}
      <div className="pt-2">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-slate-600">Marge</label>
          <span className="text-xs font-medium text-primary">{marginPct}%</span>
        </div>
        <input
          type="range"
          min="5"
          max="50"
          step="1"
          value={marginPct}
          onChange={e => onMarginChange(parseInt(e.target.value))}
          className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer accent-secondary"
        />
        <div className="flex justify-between text-[10px] text-gray-600 mt-1">
          <span>5%</span><span>25%</span><span>50%</span>
        </div>
      </div>

      {/* Apply calculated price */}
      {calculatedPrice && (
        <button
          type="button"
          onClick={() => onApplyPrice(calculatedPrice.ttc)}
          className="w-full bg-secondary/10 border border-secondary/40 text-secondary rounded-sm px-4 py-2.5 text-sm hover:bg-secondary/15 transition-colors flex items-center justify-center gap-2"
        >
          <DollarSign className="w-4 h-4" />
          Appliquer {calculatedPrice.ttc} EUR TTC comme prix de vente
        </button>
      )}
    </div>
  );
}
